import { useMode } from "../context/ModeContext";
import { useEffect, useState } from "react";

/**
 * Animated mode-wash shades — three large radial-gradient blobs that
 * continuously DRIFT across the background while a faster HEARTBEAT
 * pulse compounds on top of the motion. The two animations live on
 * different DOM layers so they multiply (outer wrapper handles
 * translate-only drift, inner blob handles scale-only heartbeat).
 *
 * Drift is slow (40–70 s per loop, ease-in-out, one of 8 path variants
 * per blob, re-rolls every 22–34 s). Heartbeat is fast (1.2–1.6 s per
 * beat, with a lub-dub double-pump per cycle). Opacity stays steady.
 */
const DRIFT_VARIANTS = [
  "shadeDriftHoriz",
  "shadeDriftVert",
  "shadeDiagonal",
  "shadeOrbit",
  "shadeFigure8",
  "shadeLazyArc",
  "shadeSpiral",
  "shadeWave",
];

const _r = (min, max) => Math.random() * (max - min) + min;

function rollDrift() {
  const name = DRIFT_VARIANTS[Math.floor(Math.random() * DRIFT_VARIANTS.length)];
  return {
    name,
    duration: _r(40, 70),                          // slow path
    delay:    -_r(0, 30),                           // out-of-phase
    top:      _r(-15, 70) + "%",
    left:     _r(-15, 70) + "%",
  };
}

function rollHeartbeat() {
  // 1.2–1.6 s per beat — slightly faster than a resting human heart so
  // it reads as "alive" without feeling anxious. Each blob has its own
  // rhythm so they don't beat in lockstep.
  return {
    duration: _r(1.2, 1.6),
    delay:    -_r(0, 1.5),
  };
}

export default function ModeWashShades() {
  let accent = "#FF4F27";
  try {
    const m = useMode();
    accent = m.config?.accent || accent;
  } catch (_) { /* outside provider */ }

  const [drifts, setDrifts] = useState(() => [rollDrift(), rollDrift(), rollDrift()]);
  const [beats]            = useState(() => [rollHeartbeat(), rollHeartbeat(), rollHeartbeat()]);

  // Re-roll drift every 22–34 s per blob, desynced.
  useEffect(() => {
    const timers = [0, 1, 2].map((idx) => setInterval(() => {
      setDrifts((prev) => {
        const next = [...prev];
        next[idx] = rollDrift();
        return next;
      });
    }, _r(22000, 34000)));
    return () => timers.forEach(clearInterval);
  }, []);

  return (
    <>
      <style>{`
        /* ─── Drift paths (slow, translate-only, opacity steady) ─── */
        @keyframes shadeDriftHoriz {
          0%   { transform: translate3d(-14vw,  0,    0); }
          50%  { transform: translate3d( 20vw,  3vh,  0); }
          100% { transform: translate3d(-14vw,  0,    0); }
        }
        @keyframes shadeDriftVert {
          0%   { transform: translate3d(0,    -10vh, 0); }
          50%  { transform: translate3d(2vw,   18vh, 0); }
          100% { transform: translate3d(0,    -10vh, 0); }
        }
        @keyframes shadeDiagonal {
          0%   { transform: translate3d(-12vw, -10vh, 0); }
          50%  { transform: translate3d( 16vw,  14vh, 0); }
          100% { transform: translate3d(-12vw, -10vh, 0); }
        }
        @keyframes shadeOrbit {
          0%   { transform: translate3d(-14vw, -10vh, 0); }
          25%  { transform: translate3d( 14vw,  -6vh, 0); }
          50%  { transform: translate3d( 16vw,  14vh, 0); }
          75%  { transform: translate3d(-12vw,  16vh, 0); }
          100% { transform: translate3d(-14vw, -10vh, 0); }
        }
        @keyframes shadeFigure8 {
          0%   { transform: translate3d(-14vw,  -8vh, 0); }
          25%  { transform: translate3d(  6vw,  12vh, 0); }
          50%  { transform: translate3d( 18vw,  -8vh, 0); }
          75%  { transform: translate3d( -2vw,  12vh, 0); }
          100% { transform: translate3d(-14vw,  -8vh, 0); }
        }
        @keyframes shadeLazyArc {
          0%   { transform: translate3d(-16vw,   4vh, 0); }
          33%  { transform: translate3d( -2vw, -12vh, 0); }
          66%  { transform: translate3d( 16vw,   2vh, 0); }
          100% { transform: translate3d(-16vw,   4vh, 0); }
        }
        @keyframes shadeSpiral {
          0%   { transform: translate3d( 0,    0,    0)  rotate(0deg);   }
          25%  { transform: translate3d( 10vw, 4vh,  0)  rotate(90deg);  }
          50%  { transform: translate3d(-2vw,  14vh, 0)  rotate(180deg); }
          75%  { transform: translate3d(-12vw, 4vh,  0)  rotate(270deg); }
          100% { transform: translate3d( 0,    0,    0)  rotate(360deg); }
        }
        @keyframes shadeWave {
          0%   { transform: translate3d(-16vw,  0,    0); }
          25%  { transform: translate3d( -4vw, -8vh,  0); }
          50%  { transform: translate3d(  8vw,  4vh,  0); }
          75%  { transform: translate3d( 16vw, -3vh,  0); }
          100% { transform: translate3d(-16vw,  0,    0); }
        }

        /* ─── Heartbeat (fast, scale-only, lub-dub double-pump) ─── */
        @keyframes shadeHeartbeat {
          0%    { transform: scale(1);     }
          12%   { transform: scale(1.10);  }   /* "lub" */
          22%   { transform: scale(1);     }
          34%   { transform: scale(1.07);  }   /* "dub" */
          44%   { transform: scale(1);     }
          100%  { transform: scale(1);     }   /* long rest */
        }
        /* Accessibility + perf: no endless motion for users who opted out */
        @media (prefers-reduced-motion: reduce) {
          [data-testid="mode-wash-shades"] * { animation: none !important; }
        }
      `}</style>

      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
        data-testid="mode-wash-shades"
        data-accent={accent}
      >
        {drifts.map((d, idx) => {
          const beat    = beats[idx];
          const size    = idx === 0 ? 55 : idx === 1 ? 45 : 38;
          const opacity = idx === 0 ? 0.30 : idx === 1 ? 0.22 : 0.16;
          return (
            /* OUTER LAYER — slow positional drift only */
            <div
              key={`drift-${idx}-${d.name}`}
              className="absolute"
              data-testid={`mode-wash-blob-${idx}`}
              data-drift={d.name}
              style={{
                width:      `${size}vw`,
                height:     `${size}vw`,
                top:        d.top,
                left:       d.left,
                animation:  `${d.name} ${d.duration}s ease-in-out ${d.delay}s infinite`,
                willChange: "transform",
              }}
            >
              {/* INNER LAYER — heartbeat scale pulse.
                  NOTE: no CSS blur here — a blur filter on a continuously
                  animated 50vw layer forces per-frame GPU re-rasterization
                  and makes the whole app stutter. The radial gradient's own
                  soft falloff gives the same look for free. */}
              <div
                className="w-full h-full rounded-full"
                style={{
                  opacity,
                  background: `radial-gradient(circle at 50% 50%, ${accent}cc 0%, ${accent}55 30%, transparent 68%)`,
                  animation:  `shadeHeartbeat ${beat.duration}s ease-in-out ${beat.delay}s infinite`,
                  transition: "background 700ms ease-out",
                  willChange: "transform",
                }}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}
